import { defaultLocale, type Locale, localizations } from '@shared-core/features/settings'
import { getSafeLocalStorage } from '@shared-core/utils/safe-storage'
import { getTelegramWebApp } from '@web/libs/tg/platform'

const storageKey = 'web-language'

const supportedLanguages = new Set<string>(localizations.map((item) => item.code))

// Коды языков Telegram и браузера — ISO (kk, pt-BR), а наши локали местами другие (kz, pt-br).
// Соответствие ISO описано для мини-аппов в исходном проекте (getMiniAppLanguageCode).
function toIsoCode(locale: Locale): string {
  return locale.toLowerCase()
}

function toSupportedLanguage(language: string | undefined): string | undefined {
  const normalized = language?.toLowerCase()

  if (!normalized) return undefined

  // Полное совпадение вперёд короткого: иначе es-AR отдал бы es, а не es-ar
  const exact = localizations.find((item) => normalized === item.code || normalized === toIsoCode(item.code))

  if (exact) return exact.code

  const shortLanguage = normalized.split('-')[0]

  return localizations.find(
    (item) => shortLanguage === item.code || shortLanguage === toIsoCode(item.code).split('-')[0],
  )?.code
}

export function getWebLocaleFormatted(): string {
  const stored = getSafeLocalStorage()?.getItem(storageKey)

  if (stored && supportedLanguages.has(stored)) {
    return stored
  }

  // В TMA язык системы (navigator.language) часто не совпадает с языком Telegram —
  // пользователь ждёт язык Telegram
  const telegramLanguage = toSupportedLanguage(getTelegramWebApp()?.initDataUnsafe?.user?.language_code)

  return telegramLanguage ?? toSupportedLanguage(navigator.language) ?? defaultLocale
}

export function getWebLocaleISO(): string {
  return navigator.language || `${defaultLocale}-${defaultLocale.toUpperCase()}`
}

export function resolveWebLanguage(locale: string): Locale {
  if (supportedLanguages.has(locale)) {
    return locale as Locale
  }

  return defaultLocale
}

export function persistWebLanguage(language: Locale): void {
  getSafeLocalStorage()?.setItem(storageKey, language)
}
